import { cn } from '@hero/web-lib';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/Popover';
import { PhoneIcon, MailIcon } from '@hero/web-icons';
export interface ContactItem {
  name: string;
  phone?: string;
  email?: string;
  isPrimary?: boolean;
}

interface ContactsTooltipDisplayProps {
  /** Array of contacts to display in the popover */
  contacts?: ContactItem[];
  /** Fallback name to display when no contacts exist */
  fallbackName?: string;
  /** Custom className for the trigger element wrapper */
  className?: string;
  /** Popover header title (translated) */
  headerTitle?: string;
  /** Primary label text (translated) */
  primaryLabel?: string;
  /** Default contact label (translated) */
  defaultContactLabel?: string;
}

/**
 * ContactsTooltipDisplay - Reusable component for displaying contacts with a clickable popover
 *
 * Shows a clickable contact name that opens a popover with all contact details (phone and email).
 * If no contacts exist, displays the fallback name (usually current user).
 *
 * @example
 * ```tsx
 * <ContactsTooltipDisplay
 *   contacts={[
 *     { name: "Ahmed Mohamed", phone: "+966501234567", email: "ahmed@example.com", isPrimary: true },
 *     { name: "Sara Ali", phone: "+966509876543" }
 *   ]}
 *   fallbackName="Current User"
 *   headerTitle="Contacts"
 *   primaryLabel="Primary"
 * />
 * ```
 */
export function ContactsTooltipDisplay({
  contacts,
  fallbackName,
  className,
  headerTitle = 'Contacts',
  primaryLabel = 'Primary',
  defaultContactLabel = 'Default Contact',
}: ContactsTooltipDisplayProps) {
  // Get primary contact or first contact
  const primaryContact = contacts?.find(c => c.isPrimary) || contacts?.[0];
  const displayName = primaryContact?.name || fallbackName || 'No Contact';
  const hasContacts = contacts && contacts.length > 0;
  const contactCount = contacts?.length || 0;
  const showCounter = contactCount > 1;

  return (
    <div className={cn('flex flex-row items-center gap-1.5', className)}>
      <PhoneIcon className="size-4 text-gray-400 flex-shrink-0" />
      <Popover>
        <PopoverTrigger asChild>
          <span className="text-gray-500 text-xs cursor-pointer hover:text-gray-700 transition-colors flex items-center gap-0.5">
            <span className="underline decoration-dotted underline-offset-2">{displayName}</span>
            {showCounter && (
              <span className="text-[10px] text-gray-400 font-normal">+{contactCount}</span>
            )}
          </span>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          className="bg-gray-900 text-white border-gray-700 max-w-xs p-3">
          <div className="space-y-2">
            <p className="font-semibold text-xs mb-2 border-b border-gray-700 pb-1">
              {headerTitle}
            </p>
            {hasContacts ? (
              contacts!.map((contact, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'flex flex-col gap-1',
                    idx > 0 && 'mt-3 pt-3 border-t border-gray-700',
                  )}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">{contact.name}</span>
                    {contact.isPrimary && (
                      <span className="text-[10px] text-gray-400">({primaryLabel})</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {contact.phone && (
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="size-3 text-gray-500 flex-shrink-0" />
                        <span className="text-xs text-gray-400" dir="ltr">
                          {contact.phone}
                        </span>
                      </div>
                    )}
                    {contact.email && (
                      <div className="flex items-center gap-1.5">
                        <MailIcon className="size-3 text-gray-500 flex-shrink-0" />
                        <span className="text-xs text-gray-400">{contact.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium">{fallbackName || 'No Contact'}</span>
                <span className="text-xs text-gray-400">({defaultContactLabel})</span>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
